import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToast } from '../../Utils/toastContext';

const HIGHLIGHT_CLASS =
  'bg-amber-100 border-l-4 border-amber-400 shadow-sm transform-gpu scale-[1.01]';

const AMLTableBusinessWays = ({ responses = [] }) => {
  const getRiskBadge = (level) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';

    if (level === 'Low') return `${base} bg-emerald-50 text-emerald-700`;
    if (level === 'Medium') return `${base} bg-amber-50 text-amber-700`;
    if (level === 'High') return `${base} bg-red-50 text-red-700`;

    return `${base} bg-gray-100 text-gray-600`;
  };

  return (
    <div className="mt-4 bg-white border border-slate-100 text-xs rounded-2xl shadow-sm overflow-hidden">
      {/* HEADER */}
      <div className="px-5 py-3 border-b bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          4.4 Ways in which business is conducted
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          {/* TABLE HEAD */}
          <thead>
            <tr className="text-xs uppercase text-slate-500 bg-slate-50">
              <th className="text-left px-5 py-3">Interaction</th>
              <th className="text-left px-5 py-3">Inherent Risk</th>
              <th className="text-left px-5 py-3">Risk Rating</th>
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody className="divide-y text-xs">
            {/* ROW 1 */}
            <tr
              className={`hover:bg-slate-50 transition ${
                (responses || [])
                  .find((q) => q.question_code === 'interaction_modes')
                  ?.answer?.option_codes?.includes('face_to_face')
                  ? HIGHLIGHT_CLASS
                  : ''
              }`}
              aria-selected={(responses || [])
                .find((q) => q.question_code === 'interaction_modes')
                ?.answer?.option_codes?.includes('face_to_face')}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Face-to-face meetings
              </td>
              <td className="px-5 py-4 text-slate-600">Lower risk</td>
              <td className="px-5 py-4">
                <span className={getRiskBadge('Low')}>Low</span>
              </td>
            </tr>

            {/* ROW 2 */}
            <tr
              className={`hover:bg-slate-50 transition ${
                (responses || [])
                  .find((q) => q.question_code === 'interaction_modes')
                  ?.answer?.option_codes?.includes('representative_only')
                  ? HIGHLIGHT_CLASS
                  : ''
              }`}
              aria-selected={(responses || [])
                .find((q) => q.question_code === 'interaction_modes')
                ?.answer?.option_codes?.includes('representative_only')}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Through a representative, and never meet the actual customer
              </td>
              <td className="px-5 py-4 text-slate-600 space-y-1">
                <p>
                  • Could be higher risk if there is not a good reason for this
                  approach.
                </p>
                <p>
                  • Corporates will often use managing agents (employees or
                  specialists), and this is not considered high risk if it is
                  usual practice for that entity
                </p>
              </td>
              <td className="px-5 py-4 text-slate-600">
                Depends on rationale, further questions needed{' '}
              </td>
            </tr>

            {/* ROW 3 */}
            <tr
              className={`hover:bg-slate-50 transition ${
                (responses || [])
                  .find((q) => q.question_code === 'interaction_modes')
                  ?.answer?.option_codes?.includes('video_only')
                  ? HIGHLIGHT_CLASS
                  : ''
              }`}
              aria-selected={(responses || [])
                .find((q) => q.question_code === 'interaction_modes')
                ?.answer?.option_codes?.includes('video_only')}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Only through video calls with no in person meeting{' '}
              </td>
              <td className="px-5 py-4 text-slate-600 space-y-1">
                <p>
                  • Could be higher risk if there is not a good reason for this
                  approach.
                </p>
                <p>• More common with foreign</p>
              </td>
              <td className="px-5 py-4 text-slate-600">
                Medium (depends on rationale)
              </td>
            </tr>

            {/* ROW 4 */}
            <tr
              className={`hover:bg-red-50 transition ${
                (responses || [])
                  .find((q) => q.question_code === 'interaction_modes')
                  ?.answer?.option_codes?.includes('online_only')
                  ? HIGHLIGHT_CLASS
                  : ''
              }`}
              aria-selected={(responses || [])
                .find((q) => q.question_code === 'interaction_modes')
                ?.answer?.option_codes?.includes('online_only')}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Fully online (no meeting/video)
              </td>
              <td className="px-5 py-4 text-red-600 font-medium">
                High risk flag if will not agree to a video call meeting{' '}
              </td>
              <td className="px-5 py-4">
                <span className={getRiskBadge('High')}>High Risk</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
const AMLTableClientBusinessType = ({ responses = [] }) => {
  const HIGHLIGHT_CLASS =
    'bg-amber-100 border-l-4 border-amber-400 shadow-sm transform-gpu scale-[1.01]';

  const getSelected =
    (responses || []).find((q) => q.question_code === 'customer_profile_mix')
      ?.answer || {};

  const selectedOptions = getSelected?.option_codes || [];
  const percentages = Object.fromEntries(
    (getSelected?.selected_options || []).map((opt) => [
      opt.code,
      opt.percent ?? 0,
    ]),
  ); // expected format: { owner_occupier: 100, investment_landlord: 0 ... }

  const rows = [
    {
      key: 'owner_occupier',
      label: 'Owner / Occupier of the property',
      risk: 'Lower risk – personal use transactions',
      rating: 'Low',
    },
    {
      key: 'investment_landlord',
      label: 'Investment landlord',
      risk: 'Moderate risk – income generating intent',
      rating: 'Medium',
    },
    {
      key: 'business_landlord',
      label: 'Business landlord',
      risk: 'Higher risk – commercial exposure',
      rating: 'Medium',
    },
    {
      key: 'small_scale_professional_developer', // ✅ FIXED
      label: 'Small scale professional developer',
      risk: 'Moderate risk depending on scale',
      rating: 'Medium',
    },
    {
      key: 'large_corporate_developer', // ✅ FIXED
      label: 'Large corporate developer',
      risk: 'Complex structures may increase risk',
      rating: 'High',
    },
  ];

  const getRiskBadge = (level) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';

    if (level === 'Low') return `${base} bg-emerald-50 text-emerald-700`;
    if (level === 'Medium') return `${base} bg-amber-50 text-amber-700`;
    if (level === 'High') return `${base} bg-red-50 text-red-700`;

    return `${base} bg-gray-100 text-gray-600`;
  };

  return (
    <div className="mt-4 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      {/* HEADER */}
      <div className="px-5 py-3 border-b bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          4.6 How clients are conducting business
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Select all that apply with approximate percentages
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          {/* HEAD */}
          <thead>
            <tr className="text-xs uppercase text-slate-500 bg-slate-50">
              <th className="text-left px-5 py-3">Client Type</th>
              <th className="text-left px-5 py-3">Approx %</th>
              <th className="text-left px-5 py-3">Inherent Risk</th>
              <th className="text-left px-5 py-3">Risk Rating</th>
            </tr>
          </thead>

          {/* BODY */}
          <tbody className="divide-y text-xs">
            {rows.map((row) => {
              const isSelected = selectedOptions.includes(row.key);
              const percent = percentages[row.key] ?? 0;

              return (
                <tr
                  key={row.key}
                  className={`transition-all duration-200 ${
                    isSelected ? HIGHLIGHT_CLASS : ''
                  }`}
                >
                  <td
                    className={`px-5 py-4 ${
                      isSelected
                        ? 'font-semibold text-slate-900'
                        : 'text-slate-700'
                    }`}
                  >
                    {row.label}
                  </td>

                  <td
                    className={`px-5 py-4 ${
                      isSelected ? 'font-bold text-amber-700' : 'text-slate-600'
                    }`}
                  >
                    {percent}%
                  </td>

                  <td className="px-5 py-4 text-slate-600">{row.risk}</td>

                  <td className="px-5 py-4">
                    <span className={getRiskBadge(row.rating)}>
                      {row.rating}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* SUMMARY */}
      <div className="px-5 py-4 bg-slate-50 border-t text-xs text-slate-700">
        <strong>Summary:</strong>{' '}
        {selectedOptions.length === 1 &&
        selectedOptions.includes('owner_occupier')
          ? 'Client base consists entirely of owner/occupiers, indicating a lower overall risk profile.'
          : 'Client base includes a mix of business types. Risk varies depending on structure and intent.'}
      </div>
    </div>
  );
};
const AMLTableServicesProvided = ({ responses = [] }) => {
  const selected =
    (responses || []).find((q) => q.question_code === 'services_provided')
      ?.answer?.option_codes || [];

  return (
    <div className="mt-4 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      {/* HEADER */}
      <div className="px-5 py-3 border-b bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          4.1 Services Provided
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          {/* TABLE HEAD */}
          <thead>
            <tr className="text-xs uppercase text-slate-500 bg-slate-50">
              <th className="text-left px-5 py-3">Service Provided</th>
              <th className="text-left px-5 py-3">Inherent Risk</th>
              <th className="text-left px-5 py-3">Risk Mitigations</th>
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody className="divide-y">
            {/* ROW 1 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('buyers_agent')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Buyer’s Agent
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Often work for trusts and may not know the true beneficial
                    owner
                  </li>
                  <li>Often Incentivised to push towards completion</li>
                  <li>
                    Can allow a money-launderer to remain anonymous from the
                    other parties to the transaction
                  </li>
                  <li>Presents a respectable front to all other parties</li>
                  <li>
                    Allow foreign buyers to have access to the Australian market
                    without a physical
                  </li>
                </ul>
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>CDD via Ratified for buyers and trustees</li>
                  <li>Enhanced checks for high-risk customers</li>
                </ul>
              </td>
            </tr>

            {/* ROW 2 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('sellers_agent')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Seller’s Agent
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>Sale of a property gives the seller ‘clean’ cash</li>
                  <li>
                    Often provide trust accounts for receipt of deposits which
                    can be exploited by money launderers.
                  </li>
                  <li>
                    Agent is only paid on settlement, meaning there is pressure
                    to complete transactions despite red flags.
                  </li>
                  <li>
                    The seller’s agent’s customer is the seller, meaning no
                    money-laundering checks on the buyers are required
                  </li>
                  <li>
                    Seller’s Agent does not generally seek to understand funds
                    used to purchase the property by the Seller, and nor are
                    they obliged to
                  </li>
                </ul>
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified to conduct customer due diligence on
                    identity of Buyer, and any trustees representing the Buyer
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>

            {/* ROW 3 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('leasing_agent_long_term')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Leasing Agent (30+ years)
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>Generally lower risk than property sales</li>
                  <li>
                    Higher risk if the agent receives cash payments, and pays
                    the landlord in cash
                  </li>
                  <li>
                    Leasing agents facilitate the production of clean money
                    through the property which may have been bought with dirty
                    money
                  </li>
                </ul>
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified to conduct customer due diligence on
                    identity of Buyer, and any trustees representing the Buyer
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                  <li>Ongoing monitoring</li>
                </ul>
              </td>
            </tr>

            {/* ROW 4 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('short_term_or_property_management')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Short-term leases / Property management
              </td>

              <td className="px-5 py-4 text-slate-600 font-medium">
                Outside the scope of the AML/CTF Act
              </td>

              <td className="px-5 py-4 text-slate-400 text-xs italic">
                Not applicable
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
const AMLTableCustomerType = ({ agencyName, responses = [] }) => {
  const selected =
    (responses || []).find((q) => q.question_code === 'customer_legal_status')
      ?.answer?.option_codes || [];
  return (
    <div className="mt-4 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      {/* HEADER */}
      <div className="px-5 py-3 border-b bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-800">
          4.2 Who the customer of the {agencyName} is
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          {/* TABLE HEAD */}
          <thead>
            <tr className="text-xs uppercase text-slate-500 bg-slate-50">
              <th className="text-left px-5 py-3">Type of customer</th>
              <th className="text-left px-5 py-3">Inherent Risk</th>
              <th className="text-left px-5 py-3">Risk rating</th>
              <th className="text-left px-5 py-3">Risk Mitigations</th>
            </tr>
          </thead>

          {/* TABLE BODY */}
          <tbody className="divide-y">
            {/* ROW 1 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('individuals')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Individuals
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Lower risk if meeting in person and understand their source
                    of wealth
                  </li>
                  <li>
                    If a high net worth individual it can be hard to understand
                    the source of funds
                  </li>
                  <li>
                    If a foreign national it can also be hard to understand the
                    source of funds
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Low to High, depending on the customer{' '}
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified to conduct customer due diligence on
                    identity of Buyer, and any trustees representing the Buyer
                  </li>
                  <li>PEP/sanctions check by Ratified service</li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service.
                  </li>
                </ul>
              </td>
            </tr>

            {/* ROW 2 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('sole_traders')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Sole Traders
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Lower risk if meet in person and understand their business
                  </li>
                  <li>
                    Sole traders have less reporting obligations than companies
                    and so can more easily stay under the radar and be used to
                    launder money
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Can be high if source of wealth not understood
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to identify the individual and
                    understand sole trader relationship.
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>

            {/* ROW 3 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('registered_companies_au')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Registered companies – Australian
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Can check for information about the directors and
                    shareholders on ASIC
                  </li>
                  <li>Larger companies have to publish accounts</li>
                  <li>
                    Limited information available on trading history and
                    accounts for SMEs
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Low, but higher risk where a small company with limited
                information available
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to identify the directors and owners
                  </li>
                  <li>
                    Ratified service does ASIC and ATO checks on the company
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>

            {/* ROW 4 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('registered_companies_foreign')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Registered companies – Foreign
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    May not be able to easily find online information about the
                    company
                  </li>
                  <li>
                    Due to unfamiliarity with foreign incorporation documents it
                    will be easier for foreign entities to commit fraud against{' '}
                    {agencyName}
                  </li>
                  <li>
                    Some foreign jurisdictions permit opaque ownership of
                    companies which makes hiding the true owners easier
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Can be high{' '}
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to identify the named directors and
                    owners
                  </li>
                  <li>
                    Request certification of incorporation documents and
                    extracts from registry naming directors
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>
            {/* ROW 5 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('unregistered_companies')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Unregistered Companies
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Less information available online about directors and
                    shareholders{' '}
                  </li>
                  <li>
                    More reliant on information provided by the person, which
                    makes fraud easier{' '}
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Can be high{' '}
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to identify the named directors and
                    owners
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>
            {/* ROW 6 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('partnerships')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Partnerships
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Depending on the jurisdiction, ownership can be more opaque
                    through a partnership
                  </li>
                  <li>
                    Often, one partner will represent the partnership in person
                    or online, making hiding the identity of the other partners
                    easier
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Low if Australian – can be high of foreign partnerships where
                ownership is not understood
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to identify the named partners{' '}
                  </li>
                  <li>
                    Request list of partners and certificate of incorporation
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>
            {/* ROW 7 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('trusts')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">Trusts</td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>Can be used to hide the ultimate owner</li>
                  <li>
                    Appointment of professional trustees can create illusion of
                    legitimacy
                  </li>
                  <li>
                    A common ownership structure, but can be used to confuse
                    actual ownership
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Low where Ratified can provide information on trustees and
                beneficiaries. Can be high if structure not understood.
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>
                    Use of Ratified service to understand trust structure and
                    identify trustees and beneficiaries
                  </li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>
            {/* ROW 8 */}
            <tr
              className={`hover:bg-slate-50 transition align-top ${
                selected.includes('government_bodies')
                  ? 'bg-yellow-50 border-l-4 border-yellow-300'
                  : ''
              }`}
            >
              <td className="px-5 py-4 font-medium text-slate-800">
                Government Bodies
              </td>
              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>If Australian, then less risk</li>
                  <li>
                    If foreign then could be state-sponsored funding of
                    terrorism
                  </li>
                </ul>
              </td>
              <td className="px-5 py-4 text-slate-600 font-medium">
                Low if Australian or low risk jurisdiction Higher if high-risk
                jurisdiction
              </td>

              <td className="px-5 py-4 align-top">
                <ul className="list-disc pl-4 space-y-1 text-slate-600 text-xs leading-relaxed">
                  <li>PEP checks</li>
                  <li>
                    If higher risk, additional identity information and source
                    of wealth questions and evidence through the Ratified
                    service
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
function CDDFlowSection() {
  const steps = [
    {
      title: 'Log into Ratified Service',
      desc: 'Access the system using your secure credentials.',
    },
    {
      title: 'Start Onboarding',
      desc: "Click on 'New Customer Onboarding' to begin the process.",
    },
    {
      title: 'Select Account Type',
      desc: 'Choose the appropriate category for the customer.',
      items: [
        'Individual',
        'Corporate',
        'Trust',
        'Partnership',
        'Government / Association',
      ],
    },
    {
      title: 'Workflow Initialization',
      desc: 'The system automatically loads the correct onboarding workflow.',
    },
    {
      title: 'Send Onboarding Link',
      desc: 'Agent shares a secure onboarding link for customer completion.',
    },
  ];

  return (
    <div className="w-full tex-sm bg-white font-medium rounded-2xl shadow-sm border px-4 py-2 mt-2">
      {/* Header */}
      <div className="text-left mb-4">
        <p className="text-black mt-2 text-sm">
          Conducting Customer Due Diligence using the Ratified service
        </p>
      </div>

      {/* Steps */}
      <div className="max-w-4xl mx-auto space-y-6">
        {steps.map((step, index) => (
          <div
            key={index}
            className="flex gap-4 sm:gap-6 p-5 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all bg-gray-50"
          >
            {/* Step Number */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold">
                {index + 1}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-gray-900">
                {step.title}
              </h3>
              <p className="text-xs text-gray-600 mt-1">{step.desc}</p>

              {/* Account Types */}
              {step.items && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {step.items.map((item, i) => (
                    <span
                      key={i}
                      className="text-xs sm:text-sm bg-white border border-gray-200 text-gray-700 px-3 py-1 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function MonitoringSection({ agencyName }) {
  return (
    <section className="mb-8">
      {/* Header */}
      <h3 className="font-semibold text-base mb-2">8. Monitoring</h3>

      {/* Main Card */}
      <div className="space-y-6">
        {/* Transaction Monitoring */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
            <h4 className="font-semibold text-gray-900 text-sm">
              Transaction Monitoring
            </h4>
          </div>

          <div className="p-4 text-xs sm:p-5 space-y-4 text-gray-700 leading-relaxed">
            <p className="text-justify">
              During the life of the deal{' '}
              <span className="font-semibold">{agencyName}</span> will continue
              to consider the risk profile of the customer and the deal as more
              information arises or customer instructions change.
            </p>

            {/* Alert Highlight */}
            <div className="bg-red-50 border text-xs border-red-100 rounded-xl p-4">
              <p className="text-red-800 leading-relaxed">
                <span className="font-semibold">Red flags</span> are escalated
                immediately to the Compliance Officer.
                <br />
                The Compliance Officer will assess whether the new information
                raises the need for a{' '}
                <span className="font-semibold">
                  Suspicious Matter Report (SMR)
                </span>
                .
              </p>
            </div>
          </div>
        </div>

        {/* Ongoing Customer Monitoring */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
            <h4 className="font-semibold text-gray-900 text-sm">
              Ongoing Customer Monitoring
            </h4>
          </div>

          <div className="p-4 text-xs sm:p-5 space-y-4 text-gray-700 leading-relaxed">
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> will consider
              when it is appropriate to use monitoring software on customers.
              For one-off customers (eg representing a customer in selling or
              renting their property on a single representative basis), it is
              probably not necessary to conduct continued monitoring. However,{' '}
              <span className="font-semibold">{agencyName}</span> will consider
              transaction monitoring where it is representing a customer on a
              longer term arrangement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
const AMLContent = ({ onClose, responses = [] }) => {
  const { showToast } = useToast();
  const [accepted, setAccepted] = useState(false);
  const agencyName = useSelector((state) => state.user.agencyNameMain);
  const [progress, setProgress] = useState(0);
  const contentRef = useRef(null);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight - el.clientHeight;

    const scrolled = (scrollTop / scrollHeight) * 100;
    setProgress(scrolled);
  };
  const riskColor = (level) => {
    if (level === 'Low') return 'bg-green-50 text-green-700 border-green-200';
    if (level === 'Medium')
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  useEffect(() => {
    const blockPrint = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === 'p' || e.key.toLowerCase() === 's')
      ) {
        e.preventDefault();
        showToast('Action blocked for security reasons', 'error');
      }
    };

    window.addEventListener('keydown', blockPrint);

    return () => window.removeEventListener('keydown', blockPrint);
  }, []);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        document.body.style.filter = 'blur(12px)';
      } else {
        document.body.style.filter = 'none';
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, []);
  return (
    <div
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      className="no-select fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4"
    >
      <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] rotate-[-25deg] flex flex-wrap text-[10px] leading-6">
          {Array.from({ length: 200 }).map((_, i) => (
            <div key={i} className="p-6 whitespace-nowrap text-gray-500">
              {agencyName} • {new Date().toISOString()} • CONFIDENTIAL
            </div>
          ))}
        </div>
      </div>
      <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between bg-white border-b px-6 py-4">
          <div>
            <h2 className="text-xl mt-2 py-2 font-medium text-gray-900">
              AML / CTF Programme
            </h2>
          </div>

          {/* CLOSE BUTTON (always visible) */}
          <button
            onClick={onClose}
            className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600 hover:text-black transition"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>
        <div className="h-1 w-full bg-gray-200">
          <div
            className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-150"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-6 text-sm leading-6 text-gray-800 font-serif"
        >
          {/* Section 1 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              1. Purpose and Scope
            </h3>
            <p className="text-justify">
              This <span className="font-semibold">AML/CTF</span> Compliance
              Programme (Programme) documents the policies and controls adopted
              by <span className="font-semibold">{agencyName}</span> to
              identify, mitigate and manage the risk of money laundering and
              terrorism financing <span className="font-semibold">(ML/TF)</span>
              .
            </p>
            <p className="text-justify">
              The Programme applies to all directors, partners, officers,
              employees and contractors of the {agencyName} and to all business
              activities that constitute, or are connected with, designated
              services under the AntiMoney Laundering and CounterTerrorism
              Financing Act 2006 (Cth) (
              <span className="font-semibold">AML/CTF</span> Act), including
              real estate services captured by the Tranche 2 reforms.
            </p>
            <p className="text-justify">
              The Programme is designed to be proportionate to the size, nature
              and complexity of {agencyName}’s operations and adopts a riskbased
              approach consistent with AUSTRAC guidance.
            </p>
            <p className="text-justify mb-3">
              This Programme is established and maintained in accordance with:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-semibold">AML/CTF</span> Act
              </li>
              <li>
                <span className="font-semibold">AML/CTF</span> Rules (as amended
                from time to time)
              </li>
              <li>
                AUSTRAC regulatory guidance, typologies and enforcement
                priorities
              </li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                The Risk Assessment and this Programme are provided as a service
                by Ratified to {agencyName}. However, Ratified is not giving
                legal advice and is not responsible for ultimate compliance with
                the <span className="font-semibold">AML/CTF</span> law. That
                responsibility remains with {agencyName}.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              2. Compliance Statement
            </h3>
            <p className="text-justify">
              {agencyName} takes its responsibilities to help AUSTRAC reduce
              <span className="font-semibold">(ML/TF)</span> in Australia
              seriously and intends to be bound by the spirit and the letter of
              the law.
            </p>{' '}
            <p className="text-justify">
              {agencyName} understands that compliance with the{' '}
              <span className="font-semibold">AML/CTF</span> Act is more than
              just putting in place policies and procedures, but it is also
              about pre-active compliance and providing AUSTRAC with actionable
              intelligence.
            </p>
            <p className="text-justify">
              This <span className="font-semibold">AML/CTF</span> Compliance
              Programme has been generated for {agencyName} based on the answers
              to a Risk Assessment that {agencyName} completed. However,{' '}
              {agencyName} understands that ultimate responsibility for
              producing and finalising a{' '}
              <span className="font-semibold">AML/CTF</span>
              Compliance Programme rests with it, and it has taken the time to
              review the Programme and amend it where the Programme does not
              reflect its risk appetite, mitigation measures or the reality of
              its business.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              3. <span className="font-semibold">AML/CTF</span> Governance and
              Responsibility
            </h3>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> understands
              that compliance with the{' '}
              <span className="font-semibold">AML/CTF</span> Act is not
              fulfilled by this Programme document alone, but rather requires
              operationalisation of the controls in this Programme and regular
              consideration of{' '}
              <span className="font-semibold">{agencyName}</span> ’s{' '}
              <span className="font-semibold">(ML/TF)</span> risks.{' '}
            </p>
          </section>

          {/* Section 3.1 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              3.1 Governance and Oversight – The Board / Principal
            </h3>
            <p className="text-justify">
              The Principal / Board of{' '}
              <span className="font-semibold">{agencyName}</span> has ultimate
              responsibility for <span className="font-semibold">AML/CTF</span>{' '}
              compliance and for ensuring adequate resources are allocated to
              implement and maintain this Programme. The Principal / Board has
              reviewed, amended and signed off this Programme.
            </p>{' '}
            <p className="text-justify">
              The Principal / Board shall keep in mind its responsibilities
              under the <span className="font-semibold">AML/CTF</span> laws and
              will adjust or amend this Programme wherever necessary, with
              particular focus on risk mitigation strategies.
            </p>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> is able to use
              suppliers for various parts of its{' '}
              <span className="font-semibold">AML/CTF</span> compliance (eg
              conducting customer due diligence and help with constructing this
              Programme but <span className="font-semibold">{agencyName}</span>{' '}
              understands that it cannot outsource all its responsibilities
              under the <span className="font-semibold">AML/CTF</span> laws. In
              particular, <span className="font-semibold">{agencyName}</span>{' '}
              retains ultimate responsibility for oversight and its own
              assessment of risk.
            </p>
          </section>
          {/* Section 3.2 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              3.2 AML/CTF Compliance Officer
            </h3>
            <p className="text-justify mb-3">
              <span className="font-semibold">{agencyName}</span> appoints an
              AML/CTF Compliance Officer who is responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Implementing and updating this Programme, including assessment
                and appointment of third party suppliers{' '}
              </li>
              <li>
                Reporting to the Principal / Board once a Quarter on compliance
                practice, risks identified and changes to the Programme
                recommend
              </li>
              <li>Acting as primary liaison with AUSTRAC</li>
              <li>
                Making Suspicious Matter Reports (SMRs) to AUSTRAC if
                necessary{' '}
              </li>
              <li>
                Considering and implementing effective risk mitigation
                strategies{' '}
              </li>
            </ul>
          </section>
          {/* Section 3.3 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              3.3 Team Member Responsibilities
            </h3>
            <p className="text-justify mb-3">
              All team members and contractors must:{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Comply with this Programme and related procedures</li>
              <li>Complete required AML/CTF training</li>
              <li>
                Promptly report suspicious activity or breaches of this AML/CTF
                to their line manager or the AML/CTF Compliance Officer
              </li>
            </ul>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                Failure to comply may result in disciplinary action.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              4. ML/TF Risk Assessment
            </h3>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> has completed
              a ML/TF Risk Assessment that identifies inherent risks associated
              with its business.
            </p>
          </section>
          {/* Inherent risk */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              Inherent Risk Assessment
            </h3>
            <p className="text-justify">
              Australian property is attractive for money-laundering because it
              is a high-vale legitimate asset, it can appreciate in value, it
              can be held long term with limited scrutiny, the Australian legal
              system provides high protection to property owners, Australia
              itself has a very clean reputation globally and the settlement
              process providers a veneer of respectability.
            </p>
            <p className="text-justify">
              With this in mind,{' '}
              <span className="font-semibold">{agencyName}</span> appreciates
              that its customers may try to buy, sell or even do long term
              leasing of property as part of their money laundering activities
              or as part of financing terrorism.
            </p>
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4 mt-2">
              {/* HEADER */}
              <div className="flex items-center gap-2 ">
                <span className="material-symbols-outlined text-indigo-500">
                  insights
                </span>
                <h3 className="text-sm font-semibold text-black">
                  Inherent Risk Assessment
                </h3>
              </div>

              {/* INTRO TEXT */}
              <p className="text-sm text-black leading-relaxed">
                As such there is an inherent risk in its Services, but the risk
                fluctuates depending on:
              </p>

              {/* FACTORS LIST */}
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'The services provided',
                  'Who the customer is (company vs trust vs individual)',
                  'The complexity of the transaction',
                  'The jurisdiction of the customer',
                  `How ${agencyName} oes business with a particular customer`,
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-slate-50 border border-slate-100 rounded-lg p-3 hover:shadow-sm transition"
                  >
                    <span className="material-symbols-outlined text-indigo-400 text-base mt-[2px]">
                      check_circle
                    </span>
                    <p className="text-xs text-slate-700">{item}</p>
                  </div>
                ))}
              </div>

              {/* FOOTER NOTE */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <p className="text-xs text-indigo-700 leading-relaxed">
                  This section sets out the inherent risks for the business of{' '}
                  <span className="font-semibold">{agencyName}</span>
                </p>
              </div>
            </div>
          </section>
          {/* Section 4.1 */}
          <section className="mb-6">
            <AMLTableServicesProvided responses={responses} />
          </section>
          {/* Section 4.2*/}
          <section className="mb-6">
            <AMLTableCustomerType
              agencyName={agencyName}
              responses={responses}
            />
            <div className="bg-yellow-50 text-xs border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                Any customer who has taken unusual steps to conceal the ultimate
                owner of the property, or the identity of the buyer, is higher
                risk. <span className="font-semibold">Mitigation:</span>{' '}
                Enhanced Due Diligence on our customer, including asking source
                of wealth questions.
              </p>
            </div>
          </section>
          {/* Section 4.3*/}
          <div className="bg-white p-5 rounded-2xl border shadow-sm mb-6">
            <h3 className="text-sm font-semibold mb-4">
              4.3 Nationality Risk Profile
            </h3>

            <div className="space-y-3">
              {(
                (responses || []).find(
                  (q) => q.question_code === 'client_countries',
                )?.answer?.value || []
              ).map((c, i) => {
                const country = c.option?.label || c.country || 'Unknown';
                const percent = c.percent || 0;
                const score = c.score || 5;
                const fatf = c.fatf || 'No';
                const level = score < 5 ? 'Low' : score < 7 ? 'Medium' : 'High';

                return (
                  <div
                    key={i}
                    className={`p-3 rounded-xl border flex justify-between items-center ${riskColor(
                      level,
                    )}`}
                  >
                    <div>
                      <p className="text-xs font-semibold">{country}</p>
                      <p className="text-[11px] opacity-70">
                        Basel AML: {score} | Is the country on the FATF grey or
                        blacklist or sanctioned by Australia?: {fatf}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-xs font-semibold">{percent}%</p>
                      <p className="text-[11px]">{level} Risk</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Section 4.4*/}
          <section className="mb-6">
            <AMLTableBusinessWays responses={responses} />
          </section>
          {/* Section 4.5 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">4.5 Funding</h3>
            <p className="text-justify mb-3">
              <span className="font-semibold">{agencyName}</span> shall consider
              the source of funds:
            </p>
            {/* Funding: show answer from survey if present */}
            {(() => {
              const fundQ = (responses || []).find(
                (q) => q.question_code === 'direct_client_funds',
              );
              const fundAnswer = fundQ?.answer?.value;
              const risk = fundQ?.answer?.risk_status;

              if (fundAnswer) {
                return (
                  <div className="bg-green-50 border-l-4 border-green-200 p-3 rounded mb-3 text-xs">
                    <p className="font-medium">Answer: {String(fundAnswer)}</p>
                    <p className="text-[11px] opacity-80">Risk: {risk}</p>
                  </div>
                );
              }
              return null;
            })()}
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Funding by way of a loan from a recognised bank or home loan
                provider is likely to be lower risk.
              </li>
              <li>
                Unfinanced purchase of a valuable property will be medium risk,
                and enquiring on source of funds are to be made
              </li>
              <li>
                Large amounts of cash or virtual currencies used to purchase the
                property indicate high risk.
              </li>
            </ul>
          </section>

          {/* Section 4.6 */}
          <section className="mb-6">
            <AMLTableClientBusinessType responses={responses} />
          </section>
          {/* Section 4.7 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              4.7 Complexity of deal
            </h3>
            <p className="text-justify">
              Buying, selling or leasing a property can be a simple contract
              between two entities, or it can be made much more complex.
            </p>{' '}
            <p className="text-justify">
              {agencyName} shall consider a deal which appears unnecessarily
              complex as a higher risk transaction and shall consider if it is
              suspicious enough to trigger a SMR.
            </p>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mt-2">
              <p className="text-xs text-indigo-700 leading-relaxed">
                <span className="font-semibold">Mitigation: </span>Enhanced
                Customer Due Diligence on our customer, including asking source
                of wealth questions
              </p>
            </div>
          </section>
          {/* Section 5 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              5. Customer Due Diligence (CDD)
            </h3>
            <p className="text-justify mb-3">The purpose of CDD is to </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Identify the customer, any trustees and any beneficiaries{' '}
              </li>
              <li>
                If we act for the Seller, we also need to do CDD on the Buyer
              </li>
              <li>Verify identity using reliable and independent sources</li>
              <li>
                Check the person is not a politically exposed person (PEP) or
                subject to sanctions{' '}
              </li>
            </ul>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> is using the
              Ratified service to allow simplification of the CDD process for
              its customers. The Ratified service always enables{' '}
              <span className="font-semibold">{agencyName}</span> to easily view
              trustee and beneficial information, which is especially important
              in Australia because many properties are owned via trust
              structures.{' '}
            </p>
            <p className="text-justify">
              If a customer is being represented by someone else, we need to do
              CDD on both the customer and the representative.
            </p>
            <p className="text-justify mb-3 mt-2">
              <span className="font-semibold">Timing:</span>{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Generally speaking,{' '}
                <span className="font-semibold">{agencyName}</span> shall
                conduct CDD before providing its services. Which means we acting
                for the Seller, before signing the agreement between the Seller
                and us.
              </li>
              <li>
                If we act for the Seller, and contracts are exchanged with a
                Buyer, we also need to conduct CDD on the Buyer within 28 days
                after the exchange of contracts, or at least 3 days before the
                initially agreed day for settlement (whichever is earliest).
              </li>
            </ul>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 mt-2">
              <p className="text-xs text-gray-700 leading-relaxed">
                CDD can be Enhanced (section 6 below) or Simplified (section 7
                below)
              </p>
            </div>
            <CDDFlowSection />
            <p className="text-justify mb-3 mt-2">
              <span className="font-semibold">Outcome:</span>{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                If AML/CTF checks result in a level of suspicion about the
                transaction, then an SMR should be made to AUSTRAC. See Section
                9 below for more information about how to submit an SMR.
              </li>
              <li>
                <span className="font-semibold">{agencyName}</span> might
                consider pausing its services until the suspicions are resolved,
                but <span className="font-semibold">{agencyName}</span> should
                not delay submitting an SMR.
              </li>
              <li>
                Where Customer Due Diligence cannot be completed to the
                reasonable satisfaction of the AML/CTF Compliance Officer,{' '}
                <span className="font-semibold">{agencyName}</span> must not
                provide the designated service.
              </li>
            </ul>
          </section>
          {/* Section 6 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              6. Enhanced Customer Due Diligence (ECDD)
            </h3>
            <p className="text-justify mb-3">
              ECDD is applied where a customer or transaction presents a higher
              ML/TF risk. The Ratified service automatically seeks enhanced data
              or evidence from the individual when ECDD is triggered. Triggers
              include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Issues with initial verification of the identity documents or
                confirmation by the government dataset used by Ratified
              </li>
              <li>
                Where a change of name is evident from the documents submitted.
              </li>
              <li>Politically Exposed Persons (PEPs).</li>
              <li>
                Foreign persons or foreign entities where{' '}
                <span className="font-semibold">{agencyName}</span> cannot
                establish ownership easily.
              </li>
              <li>High risk jurisdictions – see section 4.3 above.</li>
              <li>
                Complex ownership structures – eg through multiple foreign
                jurisdictions or where the structure is unusual and designed to
                hide ownership
              </li>
              <li>Unusual transactions.</li>
            </ul>
            <p className="text-justify mb-3">
              ECDD is applied where a customer or transaction presents a higher
              ML/TF risk. The Ratified service automatically seeks enhanced data
              or evidence from the individual when ECDD is triggered. Triggers
              include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Issues with initial verification of the identity documents or
                confirmation by the government dataset used by Ratified
              </li>
              <li>
                Where a change of name is evident from the documents submitted.
              </li>
              <li>Politically Exposed Persons (PEPs).</li>
              <li>
                Foreign persons or foreign entities where{' '}
                <span className="font-semibold">{agencyName}</span> cannot
                establish ownership easily.
              </li>
              <li>High risk jurisdictions – see section 4.3 above.</li>
              <li>
                Complex ownership structures – eg through multiple foreign
                jurisdictions or where the structure is unusual and designed to
                hide ownership
              </li>
              <li>Unusual transactions.</li>
            </ul>
            <p className="text-justify mb-3 mt-2">
              <span className="font-semibold">ECDD measures include:</span>{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Use of the Ratified service to establish additional identity and
                ownership verification.
              </li>
              <li>
                Source of funds and source of wealth enquiries, together with
                evidence of source of funds.{' '}
              </li>
            </ul>
            <p className="text-justify">
              Once ECDD information has been received through the Ratified
              service then the AML/CTF Compliance Officer will determine whether
              the ECDD information collected mitigates the AML risk identified.
            </p>
          </section>
          {/* Section 7 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              7. Simplified Customer Due Diligence
            </h3>
            <p className="text-justify">
              Simplified CDD may be applied only where the ML/TF risk is
              assessed as low. Simplified CDD must not be applied where
              suspicion is identified.
            </p>{' '}
            <p className="text-justify">
              To conduct simplified CDD{' '}
              <span className="font-semibold">{agencyName}</span> will use the
              core Ratified service.
            </p>
          </section>
          {/* Section 8 */}
          <section className="mb-6">
            <MonitoringSection agencyName={agencyName} />
          </section>
          {/* Section 9 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              9. Suspicious Matter Reporting (SMR)
            </h3>
            <p className="text-justify mb-3 mt-2 underline">
              <span className="font-semibold">Introduction</span>{' '}
            </p>
            <p className="text-justify">
              The core purpose of considering transactions and conducting
              AML/CTF checks on customers is to make an SMR to AUSTRAC where{' '}
              <span className="font-semibold">{agencyName}</span> has suspicions
              about the transaction. AUSTRAC is keen to stress that where there
              are any suspicions it is good practice to make an SMR. AUSTRAC
              receives about 500,000 SMRs a year (under the current Tranche 1
              only regime), so it is evident that reporting entities make SMRs
              very regularly. SMRs can be made about our customer or about the
              transaction as a whole.
            </p>
            <p className="text-justify">
              AUSTRAC stresses that it would like high quality SMRs. AUSTRAC
              also stresses that it often receives numerous SMRs from different
              reporting entities in relation to the same transaction. here are
              very strong legal protections in place to protect the
              confidentiality of SMRs and those that make the SMR, and it is to
              be noted that police cannot use an SMR as evidence – they need to
              find additional evidence on top of any SMR made.
            </p>
            <p className="text-justify mb-3 mt-2 underline">
              <span className="font-semibold">When to make an SMR</span>{' '}
            </p>
            <p className="text-justify">
              The AML/CTF Compliance Officer has to decide whether to make an
              SMR. The threshold to make an SMR is where there are reasonable
              suspicions on the transaction. Section 4 of this Programme sets
              out when suspicions might arise. An SMR must be made within{' '}
              <span className="font-semibold">24 hours</span> if the suspicion
              relates to terrorism financing and within{' '}
              <span className="font-semibold">3 business days</span> if the
              suspicion relates to money laundering or any other offence{' '}
            </p>
            <p className="text-justify mb-3">
              Where suspicion arises that a matter may involve money laundering
              or the financing of terrorism:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Team Member must report internally immediately</li>
              <li>
                The AML/CTF Compliance Officer assesses and, if required, lodges
                an SMR with AUSTRAC
              </li>
            </ul>
            <p className="text-justify mb-3 mt-2 underline">
              <span className="font-semibold">How to make an SMR</span>{' '}
            </p>
            <p className="text-justify">
              Log into your account on{' '}
              <span className="font-semibold underline text-blue-800">
                https://online.austrac.gov.au
              </span>
              , and then complete the SMR Form{' '}
            </p>
            <p className="text-justify">
              The most important section is the Narrative. Analysis of the
              suspicions and the transaction should be included:
            </p>
            <p className="text-justify mb-3">
              Please include the following in each SMR, where possible:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>What happened</li>
              <li>Why it is suspicious </li>
              <li>When the suspicion arose</li>
              <li>How the activity deviates from normal behaviour </li>
              <li>What ML/TF risk indicators are present</li>
            </ul>
            <p className="text-justify mb-3">
              As well as the following transaction details:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>Dates</li>
              <li>Accounts</li>
              <li>Counterparties </li>
              <li>Payment methods</li>
              <li>Jurisdictions involved</li>
            </ul>
            <div className="bg-yellow-50 text-xs border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                Tippingoff or informing the customer that an SMR has been made
                is strictly prohibited by law and is a criminal offence.
              </p>
            </div>
            <p className="text-justify mt-2">
              <span className="font-semibold">{agencyName}</span> conducts CDD
              on its customers. Where the customer is the seller of a property{' '}
              <span className="font-semibold">{agencyName}</span> does not
              conduct CDD on buyers. However,{' '}
              <span className="font-semibold">{agencyName}</span> monitors
              transactions involving buyers for suspicious indicators and will
              make enquiries or lodge an SMR where appropriate.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-blue-900">
                There is no requirement to stop representing the customer where
                there are suspicions of money laundering – making an SMR is the
                legal obligation.
              </p>
            </div>
          </section>
          {/* Section 10 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              10. Threshold Transaction Reporting (TTR)
            </h3>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> has a legal
              obligation to inform AUSTRAC if it receives $10,000 in physical
              cash in one transaction or multiple linked transactions. Given{' '}
              <span className="font-semibold">{agencyName}</span> does not
              accept cash from customers in the ordinary course of business, a
              TTR is not likely to be needed very often, if at all.
            </p>
            <p className="text-justify">
              The AML/CTF Compliance Officer is responsible for making a TTR.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              11. International Funds Transfer Instructions (IFTI)
            </h3>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> must make an
              IFTI report to AUSTRAC if it receives from abroad or sends money
              abroad on behalf of a customer and in connection with buying,
              selling or leasing a property. There is no minimum amount for an
              IFTI. Normally, monies in a property transaction are transferred
              by banks or conveyancers, and the estate agent does not control
              any of these funds.
            </p>
            <p className="text-justify mb-3">
              An IFTI will apply when{' '}
              <span className="font-semibold">{agencyName}</span>:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Receives a deposit from an overseas bank account into its trust
                account
              </li>
              <li>
                Pays funds overseas (e.g. refunds, commissions, or
                disbursements) on a customer’s instructions
              </li>
              <li>
                Uses a foreign escrow or settlement platform and controls the
                transfer
              </li>
              <li>Receives commission from a foreign bank account</li>
            </ul>
            <p className="text-justify mb-3">
              An IFTI does not have to be made when{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-semibold">{agencyName}</span> receives
                commission from an Australian bank account, even when the
                customer is foreign
              </li>
              <li>
                The settlement occurs via PEXA and Australian banks are involved
              </li>
            </ul>
            <div className="bg-yellow-50 text-xs border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                The AML/CTF Compliance Officer is responsible for making IFTI
                reports. The deadline is 10 business days from the money
                transfer.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">12. RecordKeeping</h3>
            <p className="text-justify mb-3">
              Records relating to CDD, transactions, AML/CTF reports and
              training are retained for at least seven years and stored securely
              and accessibly. The following list of records shall be maintained
              at a minimum:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                CDD and ECDD data records as supplied by Ratified (without
                necessarily storing copies of the actual identity documents
                sighted)
              </li>
              <li>
                Beneficial ownership and control records supplied by Ratified
              </li>
              <li>Trust and company documentation supplied by Ratified</li>
              <li>PEP and sanctions results supplied by Ratified</li>
              <li>SMRs submitted to AUSTRAC, with confidentiality controls</li>
              <li>TTR and IFTI supporting records</li>
              <li>Training, audit and independent review records</li>
            </ul>
            <p className="text-justify mb-3">
              An IFTI does not have to be made when{' '}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-semibold">{agencyName}</span> receives
                commission from an Australian bank account, even when the
                customer is foreign
              </li>
              <li>
                The settlement occurs via PEXA and Australian banks are involved
              </li>
            </ul>
            <div className="bg-yellow-50 text-xs border-l-4 border-yellow-400 p-4 rounded-lg mt-6">
              <p className="font-medium text-yellow-900">
                The AML/CTF Compliance Officer is responsible for making IFTI
                reports. The deadline is 10 business days from the money
                transfer.
              </p>
            </div>
          </section>
          {/* Section 13 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              13. AML/CTF Training Programme
            </h3>
            <p className="text-justify mb-3">
              Training is provided to all staff using the Ratified supplied
              training materials:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>On induction</li>
              <li>At least annually</li>
              <li>When laws or procedures change</li>
            </ul>
            <p className="text-justify">
              Training completion records are made and retained.
            </p>
          </section>
          {/* Section 14 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              14. Employee Due Diligence
            </h3>
            <p className="text-justify">
              <span className="font-semibold">{agencyName}</span> conducts
              appropriate screening of employees and contractors, including fit
              and proper checks where appropriate.
            </p>
          </section>
          {/* Section 15 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              15. Reporting Breaches and NonCompliance
            </h3>
            <p className="text-justify">
              Breaches of this Programme must be reported to the AML/CTF
              Compliance Officer. Remedial action is documented and
              monitored.{' '}
            </p>
          </section>
          {/* Section 16 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              16. Independent Review and Audit
            </h3>
            <p className="text-justify">
              The Programme is subject to independent review at least every two
              years or as required by AUSTRAC.
            </p>
          </section>
          {/* Section 17 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              17. Privacy, Confidentiality and Information Security
            </h3>
            <p className="text-justify">
              Personal information is handled in accordance with our privacy
              policies and internal policies.
            </p>
          </section>
          {/* Section 18 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              18. Policy Review and Update
            </h3>
            <p className="text-justify">
              This Programme is reviewed at least annually and approved by the
              Principal or the Board of the agency.
            </p>
          </section>
          {/* Section 18 */}
          <section className="mb-6">
            <h3 className="font-semibold text-base mb-2">
              19. Consequences of NonCompliance
            </h3>
            <p className="text-justify">
              Noncompliance may result in disciplinary action in accordance with
              our policies.
            </p>
          </section>
          {/* Highlight Box */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mt-6">
            <p className="font-medium text-blue-900">
              Your information is handled with strict confidentiality and used
              only for compliance and security purposes.
            </p>
          </div>
          {/* Footer / Sign-off */}
          <section className="mt-10 pt-6 border-t border-gray-300">
            <div className="flex flex-col sm:flex-row sm:justify-between text-sm text-gray-800 font-serif">
              {/* Date */}
              <div>
                <p className="font-medium">Date</p>
                <p>
                  {new Date().toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>

              {/* Signature */}
              <div className="mt-6 sm:mt-0 text-right">
                <p className="font-medium">Signed by</p>
                <p className="font-semibold">{agencyName}</p>
                <p className="text-gray-600 text-xs">
                  Authorised Representative
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AMLContent;
